import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getSafeReturnUrl } from "./utils/returnUrl";
import "./login.css";
import { useSession } from './hooks/useSession';
import { useUser } from './contexts/UserContext';
import { GoogleLogin } from '@react-oauth/google';

// ブラウザの言語設定を取得する関数（Googleログインボタン用）
const getBrowserLanguage = () => {
  const lang = navigator.language || navigator.languages?.[0] || 'en';
  if (lang.startsWith('ja')) return 'ja';
  if (lang.startsWith('th')) return 'th';
  return 'en';
};

const API_BASE_URL = '/api3000';  // ログインAPIのベースURL

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const [currentLanguage] = useState(getBrowserLanguage());
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = getSafeReturnUrl(location.state?.from);
  const { startSession } = useSession();
  const { setUser } = useUser();

  // Google OAuthの準備完了を待つ
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsGoogleReady(true);
    }, 100); // 0.1秒後にGoogle OAuthを有効化

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const loginResponse = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ email, password })
      });

      const data = await loginResponse.json();

      if (!loginResponse.ok) {
        setError(data.message || "Login Failed");
        return;
      }

      if (data.token) {
        localStorage.setItem('token', data.token);
      }

      const userData = {
        id: data.id,
        userName: data.user_name,
        auth_id: data.auth_id,
        section_id: data.section_id ?? null,
        userLocation: data.userLocation
      };

      // ユーザー情報をlocalStorageにも保存
      localStorage.setItem('userInfo', JSON.stringify(userData));

      // ユーザー情報を設定
      setUser(userData);
      
      // セッションを開始
      startSession(userData);
      navigate(returnUrl, { replace: true });

    } catch (err) {
      console.error("Error details:", err);
      setError("Server connection failed");
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    
    try {
      const googleLoginResponse = await fetch(`${API_BASE_URL}/google-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ idToken: credentialResponse.credential })
      });

      const data = await googleLoginResponse.json();

      if (!googleLoginResponse.ok) {
        setError(data.message || "Google Login Failed");
        return;
      }

      if (data.token) {
        localStorage.setItem('token', data.token);
      }

      const userData = {
        id: data.id,
        userName: data.user_name,
        auth_id: data.auth_id,
        section_id: data.section_id ?? null,
        userLocation: data.userLocation
      };

      // ユーザー情報をlocalStorageにも保存
      localStorage.setItem('userInfo', JSON.stringify(userData));

      // ユーザー情報を設定
      setUser(userData);
      
      // セッションを開始
      const sessionStarted = startSession(userData);
      
      if (sessionStarted) {
        // 状態更新を待ってからナビゲート
        setTimeout(() => {
          navigate(returnUrl, { replace: true });
        }, 50);
      } else {
        setError("Session start failed");
      }

    } catch (err) {
      console.error("Google login error:", err);
      setError("Google login failed");
    }
  };

  const handleGoogleError = (error) => {
    console.error("Google OAuth Error:", error);
    setError("Google login failed");
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold mb-6 text-center text-gray-800">Login</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input 
              type="email" 
              placeholder="Email address" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <input 
              type="password" 
              placeholder="Password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <button 
            type="submit"
            className="w-full py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors duration-200"
          >
            Login
          </button>
        </form>
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500"> OR </span>
            </div>
          </div>
          
          <div className="mt-6 flex justify-center" id="google-login-container">
            {isGoogleReady ? (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                auto_select={false}
                use_fedcm_for_button={true}
                type="standard"
                theme="outline"
                size="large"
                text="signin_with"
                shape="rectangular"
                logo_alignment="left"
                width="300"
                locale={currentLanguage}
              />
            ) : (
              <div className="bg-gray-300 text-gray-600 px-4 py-2 rounded">
                Loading Google Login...
              </div>
            )}
          </div>
        </div>
        
        {error && <p className="text-red-500 text-center mt-4">{error}</p>}
      </div>
    </div>
  );
};

export default Login;
